import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaUserShield,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaSyncAlt,
  FaCopy,
  FaCheck,
  FaChevronDown,
  FaCircleNotch,
  FaUser,
  FaEnvelope,
  FaKey,
  FaUserSlash,
  FaUserCheck,
  FaTrashAlt,
  FaExclamationTriangle,
  FaTimes,
  FaMicrosoft,
  FaIdBadge,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { ROLE_LABELS, roleLabel, assignableRoles, canModifyUser } from "../../constants/roles";
import "./UserManagement.css";
import "./UserDetail.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const getAuthToken = () =>
  sessionStorage.getItem("auth_token") ||
  localStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("auth_token") ||
  sessionStorage.getItem("token") ||
  sessionStorage.getItem("accessToken") ||
  "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

const apiCall = async (path, options = {}) => {
  const resp = await fetch(`${API_BASE}${path}`, options);
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data?.success) {
    throw new Error(data?.message || data?.error || `Request failed (${resp.status}).`);
  }
  return data;
};

const normEmail = (e) => String(e || "").trim().toLowerCase();
const roleClass = (role) => `role-${String(role || "analyst").replace(/_/g, "-")}`;

const initialsOf = (name, email) => {
  const base = String(name || "").trim();
  if (base) {
    const parts = base.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 2).toUpperCase();
  }
  const handle = String(email || "?").split("@")[0];
  return handle.slice(0, 2).toUpperCase();
};

const generatePassword = (len = 16) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*?";
  const crypto = window.crypto || window.msCrypto;
  let out = "";
  if (crypto?.getRandomValues) {
    const buf = new Uint32Array(len);
    crypto.getRandomValues(buf);
    for (let i = 0; i < len; i++) out += chars[buf[i] % chars.length];
  } else {
    for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";

const ROLE_HINTS = {
  platform_admin: "Full access to every module, settings, and user management.",
  user_admin: "Manages users and roles.",
  contributor: "Manages the IoC whitelist and blacklist.",
  analyst: "Standard investigation access; no administrative settings.",
};

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: actor } = useAuth();

  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  // section busy flags
  const [busy, setBusy] = useState(""); // "profile" | "role" | "status" | "password" | "delete"

  // editable fields
  const [nameVal, setNameVal] = useState("");
  const [roleVal, setRoleVal] = useState("analyst");
  const [pwVal, setPwVal] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [copied, setCopied] = useState(false);

  // confirm modals
  const [confirm, setConfirm] = useState(null); // "disable" | "delete" | null

  const roleOptions = useMemo(
    () => assignableRoles(actor?.role).map((v) => ({ value: v, label: ROLE_LABELS[v] })),
    [actor]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await apiCall(`/api/admin/users/${id}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      setTarget(data.user);
      setNameVal(data.user?.name || "");
      setRoleVal(String(data.user?.role || "analyst").toLowerCase());
    } catch (err) {
      setLoadError(err.message || "Failed to load user.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (ok, msg) => {
    if (ok) { setOkMsg(msg); setErrMsg(""); }
    else { setErrMsg(msg); setOkMsg(""); }
  };

  const copyToClipboard = useCallback((value) => {
    if (!value) return;
    try {
      navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  const canModify = target ? canModifyUser(actor?.role, target.role) : false;
  const isSelf = target ? normEmail(target.email) === normEmail(actor?.email) : false;
  const disabled = target ? Number(target.disabled) === 1 : false;
  const isSso = !!target?.sso_provider;

  // -------- mutations --------
  const saveProfile = async () => {
    if (nameVal.trim().length < 2) return flash(false, "Name must be at least 2 characters.");
    setBusy("profile");
    try {
      const data = await apiCall(`/api/admin/users/profile`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ email: target.email, name: nameVal.trim() }),
      });
      setTarget(data.user);
      flash(true, "Profile updated.");
    } catch (err) {
      flash(false, err.message);
    } finally {
      setBusy("");
    }
  };

  const saveRole = async () => {
    setBusy("role");
    try {
      const data = await apiCall(`/api/admin/users/role`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ email: target.email, role: roleVal }),
      });
      setTarget(data.user);
      flash(true, `Role updated to ${roleLabel(roleVal)}.`);
    } catch (err) {
      flash(false, err.message);
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (toDisabled) => {
    setBusy("status");
    try {
      const data = await apiCall(`/api/admin/users/status`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ email: target.email, disabled: toDisabled }),
      });
      setTarget(data.user);
      setConfirm(null);
      flash(true, `Account ${toDisabled ? "disabled" : "enabled"}.`);
    } catch (err) {
      flash(false, err.message);
    } finally {
      setBusy("");
    }
  };

  const resetPassword = async () => {
    if (pwVal.trim().length < 8) return flash(false, "Temporary password must be at least 8 characters.");
    setBusy("password");
    try {
      const data = await apiCall(`/api/admin/users/reset-password`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ email: target.email, tempPassword: pwVal.trim() }),
      });
      setTarget((prev) => ({ ...prev, ...(data.user || { must_change_password: 1 }) }));
      setPwVal("");
      setShowPw(false);
      flash(true, "Password reset. The user must change it on next login.");
    } catch (err) {
      flash(false, err.message);
    } finally {
      setBusy("");
    }
  };

  const deleteUser = async () => {
    setBusy("delete");
    try {
      await apiCall(`/api/admin/users`, {
        method: "DELETE",
        headers: authHeaders(),
        body: JSON.stringify({ email: target.email }),
      });
      navigate("/admin/users", { replace: true });
    } catch (err) {
      flash(false, err.message);
      setBusy("");
      setConfirm(null);
    }
  };

  // -------- render --------
  if (loading) {
    return (
      <div className="ud-page">
        <div className="ud-state"><FaCircleNotch className="um-spin" /> Loading user…</div>
      </div>
    );
  }

  if (loadError || !target) {
    return (
      <div className="ud-page">
        <button type="button" className="ud-back" onClick={() => navigate("/admin/users")}>
          <FaArrowLeft /> Back to users
        </button>
        <div className="ud-state error">
          <FaExclamationTriangle /> {loadError || "User not found."}
        </div>
      </div>
    );
  }

  const roleChanged = roleVal.toLowerCase() !== String(target.role || "").toLowerCase();
  const nameChanged = nameVal.trim() !== String(target.name || "").trim();

  return (
    <div className="ud-page">
      <button type="button" className="ud-back" onClick={() => navigate(-1)}>
        <FaArrowLeft /> Back to users
      </button>

      {/* Hero */}
      <div className="ud-hero">
        <span className={`um-avatar lg ${roleClass(target.role)}`}>{initialsOf(target.name, target.email)}</span>
        <div className="ud-hero-main">
          <div className="ud-hero-name">
            {target.name || "Unnamed user"}
            {isSelf ? <span className="um-you">You</span> : null}
          </div>
          <div className="ud-hero-email">{target.email}</div>
          <div className="ud-hero-tags">
            <span className={`um-badge ${roleClass(target.role)}`}>{ROLE_LABELS[target.role] || target.role}</span>
            <span className={`um-status ${disabled ? "is-disabled" : "is-active"}`}>
              <span className="um-status-dot" />{disabled ? "Disabled" : "Active"}
            </span>
            {Number(target.must_change_password) === 1 ? <span className="um-badge temp">Temp password</span> : null}
            {isSso ? <span className="ud-sso-badge"><FaMicrosoft /> SSO</span> : null}
          </div>
        </div>
      </div>

      {/* Toasts */}
      {okMsg ? (
        <div className="um-alert ok"><FaCheck className="um-alert-icon" /><span>{okMsg}</span>
          <button type="button" className="um-alert-x" onClick={() => setOkMsg("")}><FaTimes /></button>
        </div>
      ) : null}
      {errMsg ? (
        <div className="um-alert err"><FaExclamationTriangle className="um-alert-icon" /><span>{errMsg}</span>
          <button type="button" className="um-alert-x" onClick={() => setErrMsg("")}><FaTimes /></button>
        </div>
      ) : null}

      {!canModify ? (
        <div className="ud-readonly-note">
          <FaLock /> You can view this user but don't have permission to modify them.
        </div>
      ) : null}

      <div className="ud-grid">
        {/* Profile */}
        <section className="ud-card">
          <div className="ud-card-head"><FaUser /> <h3>Profile</h3></div>
          <div className="um-field">
            <label>Full name</label>
            <div className="um-input">
              <FaUser className="um-input-icon" />
              <input type="text" value={nameVal} onChange={(e) => setNameVal(e.target.value)} disabled={!canModify} />
            </div>
          </div>
          <div className="um-field" style={{ marginTop: 14 }}>
            <label>Email address</label>
            <div className="um-input">
              <FaEnvelope className="um-input-icon" />
              <input type="email" value={target.email} readOnly disabled />
            </div>
            <p className="um-hint" style={{ marginTop: 6 }}>Email is the account identifier and can't be changed.</p>
          </div>
          {canModify ? (
            <div className="ud-card-actions">
              <button type="button" className="um-btn primary" onClick={saveProfile} disabled={busy === "profile" || !nameChanged}>
                {busy === "profile" ? <><FaCircleNotch className="um-spin" /> Saving…</> : "Save changes"}
              </button>
            </div>
          ) : null}
        </section>

        {/* Security / password */}
        <section className="ud-card">
          <div className="ud-card-head"><FaKey /> <h3>Security</h3></div>
          <div className="ud-meta-line">
            <span>Password status</span>
            <strong>{Number(target.must_change_password) === 1 ? "Temporary — change required" : "Set by user"}</strong>
          </div>
          <div className="ud-meta-line">
            <span>Sign-in method</span>
            <strong>{isSso ? "Microsoft SSO" : "Password"}</strong>
          </div>
          {canModify && !isSso ? (
            <>
              <div className="ud-divider" />
              <div className="um-field">
                <label>Reset to a temporary password</label>
                <div className="um-input">
                  <FaLock className="um-input-icon" />
                  <input
                    type={showPw ? "text" : "password"}
                    value={pwVal}
                    placeholder="Min 8 characters"
                    onChange={(e) => setPwVal(e.target.value)}
                    autoComplete="new-password"
                  />
                  <div className="um-input-tools">
                    <button type="button" className="um-icon-btn" title="Generate" onClick={() => { setPwVal(generatePassword()); setShowPw(true); }}><FaSyncAlt /></button>
                    <button type="button" className="um-icon-btn" title={copied ? "Copied" : "Copy"} disabled={!pwVal} onClick={() => copyToClipboard(pwVal)}>{copied ? <FaCheck /> : <FaCopy />}</button>
                    <button type="button" className="um-icon-btn" title={showPw ? "Hide" : "Show"} onClick={() => setShowPw((s) => !s)}>{showPw ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>
                </div>
              </div>
              <div className="ud-card-actions">
                <button type="button" className="um-btn primary" onClick={resetPassword} disabled={busy === "password" || pwVal.trim().length < 8}>
                  {busy === "password" ? <><FaCircleNotch className="um-spin" /> Resetting…</> : <><FaKey /> Reset password</>}
                </button>
              </div>
            </>
          ) : isSso ? (
            <p className="um-hint" style={{ marginTop: 12 }}>This account signs in via SSO; its password is managed by your identity provider.</p>
          ) : null}
        </section>

        {/* Role */}
        <section className="ud-card">
          <div className="ud-card-head"><FaUserShield /> <h3>Role &amp; Permissions</h3></div>
          <div className="um-field">
            <label>Role</label>
            <div className="um-input um-select">
              <FaUserShield className="um-input-icon" />
              <select value={roleVal} onChange={(e) => setRoleVal(e.target.value)} disabled={!canModify}>
                {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <FaChevronDown className="um-select-caret" />
            </div>
          </div>
          <p className="um-hint" style={{ marginTop: 10 }}>{ROLE_HINTS[roleVal] || ""}</p>
          {canModify ? (
            <div className="ud-card-actions">
              <button type="button" className="um-btn primary" onClick={saveRole} disabled={busy === "role" || !roleChanged}>
                {busy === "role" ? <><FaCircleNotch className="um-spin" /> Updating…</> : "Update role"}
              </button>
            </div>
          ) : null}
        </section>

        {/* Account access */}
        <section className="ud-card">
          <div className="ud-card-head"><FaUserCheck /> <h3>Account Access</h3></div>
          <div className="ud-toggle-row">
            <div className="ud-toggle-text">
              <div className="ud-toggle-title">{disabled ? "Account disabled" : "Account active"}</div>
              <div className="ud-toggle-sub">
                {disabled
                  ? "This user is blocked from signing in (password and SSO)."
                  : "This user can sign in normally."}
              </div>
            </div>
            <button
              type="button"
              className={`ud-switch ${disabled ? "" : "on"}`}
              role="switch"
              aria-checked={!disabled}
              disabled={!canModify || isSelf || busy === "status"}
              title={isSelf ? "You can't disable your own account" : undefined}
              onClick={() => (disabled ? setStatus(false) : setConfirm("disable"))}
            >
              <span className="ud-switch-knob" />
            </button>
          </div>
          {isSelf ? <p className="um-hint" style={{ marginTop: 12 }}>You can't change the status of your own account.</p> : null}
        </section>

        {/* Details */}
        <section className="ud-card">
          <div className="ud-card-head"><FaIdBadge /> <h3>Details</h3></div>
          <div className="ud-meta-line"><span>User ID</span><strong className="ud-mono">{target.id}</strong></div>
          <div className="ud-meta-line"><span>Created</span><strong>{fmtDateTime(target.created_at)}</strong></div>
          <div className="ud-meta-line"><span>Last updated</span><strong>{fmtDateTime(target.updated_at)}</strong></div>
          {isSso ? <div className="ud-meta-line"><span>SSO provider</span><strong>{target.sso_provider}</strong></div> : null}
        </section>

        {/* Danger zone */}
        {canModify && !isSelf ? (
          <section className="ud-card danger">
            <div className="ud-toggle-row">
              <div className="ud-toggle-text">
                <div className="ud-toggle-title danger-title"><FaTrashAlt /> Delete this user</div>
                <div className="ud-toggle-sub">Permanently removes the account. Consider disabling instead if you may need it later.</div>
              </div>
              <button type="button" className="um-btn danger" onClick={() => setConfirm("delete")} disabled={busy === "delete"}>
                <FaTrashAlt /> Delete
              </button>
            </div>
          </section>
        ) : null}
      </div>

      {/* ===== Confirm modals ===== */}
      {confirm ? (
        <div className="um-modal-backdrop" onMouseDown={() => busy ? null : setConfirm(null)}>
          <div className="um-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            {confirm === "disable" ? (
              <>
                <div className="um-modal-head">
                  <div className="um-modal-title danger"><FaUserSlash /> Disable Account</div>
                  <button type="button" className="um-modal-x" onClick={() => setConfirm(null)}><FaTimes /></button>
                </div>
                <div className="um-modal-body">
                  <div className="ud-confirm-id">
                    <span className={`um-avatar sm ${roleClass(target.role)}`}>{initialsOf(target.name, target.email)}</span>
                    <span>{target.name || target.email}</span>
                  </div>
                  <div className="um-warn-box">
                    <FaExclamationTriangle />
                    <span>{target.name || "This user"} will be signed out and blocked from logging in (password and SSO) until re-enabled.</span>
                  </div>
                </div>
                <div className="um-modal-foot">
                  <button type="button" className="um-btn secondary" onClick={() => setConfirm(null)} disabled={busy === "status"}>Cancel</button>
                  <button type="button" className="um-btn danger" onClick={() => setStatus(true)} disabled={busy === "status"}>
                    {busy === "status" ? <><FaCircleNotch className="um-spin" /> Disabling…</> : <><FaUserSlash /> Disable Account</>}
                  </button>
                </div>
              </>
            ) : null}

            {confirm === "delete" ? (
              <>
                <div className="um-modal-head">
                  <div className="um-modal-title danger"><FaTrashAlt /> Delete User</div>
                  <button type="button" className="um-modal-x" onClick={() => setConfirm(null)}><FaTimes /></button>
                </div>
                <div className="um-modal-body">
                  <div className="ud-confirm-id">
                    <span className={`um-avatar sm ${roleClass(target.role)}`}>{initialsOf(target.name, target.email)}</span>
                    <span>{target.name || target.email}</span>
                  </div>
                  <div className="um-warn-box danger">
                    <FaExclamationTriangle />
                    <span>This permanently removes the account and cannot be undone.</span>
                  </div>
                </div>
                <div className="um-modal-foot">
                  <button type="button" className="um-btn secondary" onClick={() => setConfirm(null)} disabled={busy === "delete"}>Cancel</button>
                  <button type="button" className="um-btn danger" onClick={deleteUser} disabled={busy === "delete"}>
                    {busy === "delete" ? <><FaCircleNotch className="um-spin" /> Deleting…</> : <><FaTrashAlt /> Delete Permanently</>}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserDetail;
